import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { Mail, Lock, AlertCircle, ArrowRight, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export function LoginPage() {
  const navigate = useNavigate()
  const signIn = useAuthStore((state) => state.signIn)
  const user = useAuthStore((state) => state.user)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [specificError, setSpecificError] = useState<{
    type: 'email' | 'password' | 'account' | 'general';
    message: string;
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [resetPasswordMode, setResetPasswordMode] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [attemptCount, setAttemptCount] = useState(0)

  useEffect(() => {
    if (user) {
      navigate('/')
    }
  }, [user, navigate])

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password: string) => {
    return password.length >= 6
  }

  const showError = (message: string, type: 'email' | 'password' | 'account' | 'general' = 'general') => {
    setError(message)
    setSpecificError({ type, message })
    setToastMessage(message)
    setToastType('error')
    setShowToast(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSpecificError(null)
    setLoading(true)

    try {
      // Client-side validation for empty fields
      if (!email.trim()) {
        showError("Veuillez remplir tous les champs correctement. L'identifiant doit être une adresse email valide.", 'email')
        setLoading(false)
        return
      }

      if (!password.trim()) {
        showError("Veuillez remplir tous les champs correctement. Le mot de passe ne peut pas être vide.", 'password')
        setLoading(false)
        return
      }

      // Client-side validation for email format
      if (!validateEmail(email)) {
        showError("Veuillez remplir tous les champs correctement. L'identifiant doit être une adresse email valide.", 'email')
        setLoading(false)
        return
      }

      if (!validatePassword(password)) {
        showError("Le mot de passe doit contenir au moins 6 caractères", 'password')
        setLoading(false)
        return
      }

      // Increment attempt count
      const newAttemptCount = attemptCount + 1
      setAttemptCount(newAttemptCount)
      
      // Check if account is temporarily locked (after 5 attempts)
      if (newAttemptCount >= 5) {
        showError("Votre compte a été temporairement verrouillé suite à plusieurs tentatives infructueuses. Veuillez réessayer dans 30 minutes ou contacter le support.", 'account')
        setLoading(false)
        return
      }

      // Try to sign in directly first
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (signInError) {
        // Check error message to determine the type
        if (signInError.message.includes('Invalid login credentials')) {
          // We need to check if the email exists
          const { data: { user: emailCheck }, error: emailError } = await supabase.auth.signUp({
            email,
            password: 'temporary-check-password',
            options: { emailRedirectTo: window.location.origin }
          })

          if (emailCheck?.identities?.length === 0) {
            // Email exists but password is wrong
            showError("Le mot de passe saisi est incorrect. Veuillez réessayer ou cliquer sur 'Mot de passe oublié' pour le réinitialiser.", 'password')
          } else {
            // Email doesn't exist
            showError("Aucun compte n'existe avec cet identifiant. Veuillez vérifier votre saisie ou créer un nouveau compte.", 'email')
          }
        } else {
          showError(`Une erreur est survenue: ${signInError.message}`, 'general')
        }
      } else if (signInData.user) {
        // Successful login
        await signIn(email, password)
        navigate('/')
      }
    } catch (err: any) {
      console.error('Login error:', err)
      showError('Une erreur est survenue lors de la connexion', 'general')
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSpecificError(null)

    if (!email.trim()) {
      showError("Veuillez saisir votre adresse email pour réinitialiser votre mot de passe.", 'email')
      setLoading(false)
      return
    }

    if (!validateEmail(email)) {
      showError("Veuillez saisir une adresse email valide.", 'email')
      setLoading(false)
      return
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      setResetSent(true)
      setToastMessage('Instructions de réinitialisation envoyées à votre email')
      setToastType('success')
      setShowToast(true)
    } catch (error) {
      console.error('Error resetting password:', error)
      showError('Erreur lors de la réinitialisation du mot de passe', 'general')
    } finally {
      setLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div 
      className="fixed inset-0 flex flex-col justify-center items-center bg-cover bg-center px-4 overflow-y-auto"
      style={{ 
        backgroundImage: "url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')",
        backgroundColor: "#0056b3",
        backgroundBlendMode: "overlay",
        backgroundSize: "cover"
      }}
    >
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
          position="center"
        />
      )}

      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-6">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary to-blue-600 rounded-full flex items-center justify-center">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-3 text-center text-2xl sm:text-3xl font-extrabold text-white">
            Bienvenue sur AEM
          </h2>
          <div className="mt-2 flex items-center justify-center text-sm">
            <div className="h-px w-12 bg-white/30"></div>
            <p className="mx-4 text-white/80">
              {resetPasswordMode 
                ? 'Réinitialiser votre mot de passe' 
                : 'Connectez-vous à votre compte'}
            </p>
            <div className="h-px w-12 bg-white/30"></div>
          </div>
          <p className="mt-2 text-sm sm:text-base text-white/90">
            {resetPasswordMode ? (
              <button 
                onClick={() => setResetPasswordMode(false)}
                className="font-semibold text-white hover:text-white/80 transition-colors"
              >
                Retour à la connexion
              </button>
            ) : (
              <>
                Pas encore de compte ?{' '}
                <Link 
                  to="/register" 
                  className="font-semibold text-white hover:text-white/80 transition-colors"
                >
                  Créer un compte
                  <ArrowRight className="inline-block ml-1 h-4 w-4" />
                </Link>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="w-full max-w-md px-4">
        <div className="bg-white/90 backdrop-blur-sm py-5 sm:py-8 px-4 shadow-xl shadow-primary/20 sm:rounded-xl sm:px-10 border border-white/20">
          {resetSent ? (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="mt-2 text-xl font-medium text-gray-900">Email envoyé</h3>
              <p className="mt-1 text-sm text-gray-500">
                Veuillez vérifier votre boîte de réception et suivre les instructions pour réinitialiser votre mot de passe.
              </p>
              <Button
                variant="primary"
                className="mt-4 w-full py-3"
                onClick={() => setResetPasswordMode(false)}
              >
                Retour à la connexion
              </Button>
            </div>
          ) : resetPasswordMode ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`block w-full pl-10 pr-3 py-2 sm:py-3 border ${specificError?.type === 'email' ? 'border-red-500 ring-red-500' : 'border-gray-300'} rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow`}
                    placeholder="vous@exemple.com"
                  />
                </div>
                {specificError?.type === 'email' && (
                  <div className="mt-1 flex items-start text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{specificError.message}</span>
                  </div>
                )}
              </div>

              <div className="mt-2">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-3 flex justify-center transform hover:scale-[1.02] transition-transform"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Envoyer les instructions'
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`block w-full pl-10 pr-3 py-2 sm:py-3 border ${specificError?.type === 'email' ? 'border-red-500 ring-red-500' : 'border-gray-300'} rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow`}
                    placeholder="vous@exemple.com"
                  />
                </div>
                {specificError?.type === 'email' && (
                  <div className="mt-1 flex items-start text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{specificError.message}</span>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Mot de passe
                  </label>
                  <button
                    type="button"
                    onClick={() => setResetPasswordMode(true)}
                    className="text-xs font-medium text-primary hover:text-primary-dark transition-colors"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`block w-full pl-10 pr-10 py-2 sm:py-3 border ${specificError?.type === 'password' ? 'border-red-500 ring-red-500' : 'border-gray-300'} rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow`}
                    placeholder="••••••••"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {specificError?.type === 'password' && (
                  <div className="mt-1 flex items-start text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{specificError.message}</span>
                  </div>
                )}
              </div>

              {specificError?.type === 'account' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-red-700 text-sm">{specificError.message}</span>
                  </div>
                </div>
              )}

              <div className="mt-2">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-3 flex justify-center transform hover:scale-[1.02] transition-transform"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}